/*
 * ts-common is the basic building blocks of our typescript projects
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { AssertionException, BadImplementationException } from '../core/exceptions/exceptions.js';
import { asArray, sortArray } from './array-tools.js';
import { merge } from './merge-tools.js';
import { exists } from './tools.js';
/**
 * Retrieves a value from an object using dot notation (e.g., "user.profile.name").
 *
 * **Warning**: No error handling for missing properties - will throw if path is invalid.
 *
 * @param key - Dot-notation path (e.g., "user.profile.name")
 * @param dotNotatedObject - Object to traverse
 * @returns The value at the specified path
 */
export function getDotNotatedValue(key, dotNotatedObject) {
    const pathParts = key.split('.');
    return pathParts.reduce((value, _pathPart) => {
        return value[_pathPart];
    }, dotNotatedObject);
}
/**
 * Creates a deep clone of an object, recursively cloning all nested objects and arrays.
 *
 * Primitive values (string, number, boolean, null, undefined) are returned as-is.
 * Arrays and objects are recursively cloned. Functions and other non-POJO types are
 * not properly cloned (they are copied by reference).
 *
 * **Note**: This does not handle circular references and will cause a stack overflow
 * if the object contains cycles.
 *
 * @param obj - Object to clone
 * @returns Deep clone of the object
 */
export function deepClone(obj) {
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean' || typeof obj === 'undefined' || obj === null)
        return obj;
    if (Array.isArray(obj))
        return cloneArr(obj);
    return cloneObj(obj);
}
export function _keys(instance) {
    return Object.keys(instance);
}
export function _values(object) {
    return Object.values(object);
}
export function _entries(obj) {
    return Object.entries(obj).map(entry => ({ key: entry[0], value: entry[1] }));
}
export function sortObject(obj, sortFunction) {
    return sortArray(_keys(obj), sortFunction).reduce((toRet, key) => {
        toRet[key] = obj[key];
        return toRet;
    }, {});
}
export function _setTypedProp(instance, key, value) {
    instance[key] = value;
}
export function cloneArr(value) {
    return value.map(a => deepClone(a));
}
export function cloneObj(obj) {
    return _keys(obj).reduce((carry, key) => {
        carry[key] = deepClone(obj[key]);
        return carry;
    }, {});
}
/**
 * Compares two objects after removing specified keys from both.
 *
 * Useful for comparing objects while ignoring certain fields (e.g., timestamps,
 * IDs, or computed properties). Both objects are deep-cloned before comparison
 * to avoid mutating the originals.
 *
 * @param one - First object to compare
 * @param two - Second object to compare
 * @param keysToFilterOut - Optional array of keys to exclude from comparison
 * @returns true if objects are equal after filtering, false otherwise
 */
export function partialCompare(one, two, keysToFilterOut) {
    one = deepClone(one);
    two = deepClone(two);
    keysToFilterOut?.forEach(key => {
        delete one?.[key];
        delete two?.[key];
    });
    return compare(one, two);
}
/** Narrowing helper for RegExp (robust across realms) */
const isRegExp = (v) => Object.prototype.toString.call(v) === '[object RegExp]';
/** Compare RegExps by source, flags, and lastIndex */
const compareRegExp = (a, b) => a.source === b.source && a.flags === b.flags && a.lastIndex === b.lastIndex;
/**
 * Deep equality comparison for objects, arrays, and primitives.
 *
 * Performs recursive deep comparison with special handling for:
 * - **RegExp**: Compares source, flags, and lastIndex
 * - **Date**: Compares epoch time (getTime())
 * - **Arrays**: Element-by-element comparison
 * - **Objects**: Key-by-key comparison (optionally limited to specified keys)
 *
 * **Limitations**:
 * - Functions are not compared (throws BadImplementationException)
 * - Circular references are not handled (will cause stack overflow)
 * - Only enumerable own properties are compared
 *
 * @param one - First value to compare
 * @param two - Second value to compare
 * @param keys - Optional array of keys to compare (only these keys are checked for objects)
 * @returns true if values are deeply equal, false otherwise
 * @throws {BadImplementationException} If either value is a function
 */
export function compare(one, two, keys) {
    const typeofOne = typeof one;
    const typeofTwo = typeof two;
    if (typeofOne !== typeofTwo)
        return false;
    if (one === undefined && two === undefined)
        return true;
    if (one === undefined || two === undefined)
        return false;
    if (one === null && two === null)
        return true;
    if (one === null || two === null)
        return false;
    // ----- SPECIAL CASES -----
    // RegExp: compare semantic fields (keys are non-enumerable)
    if (isRegExp(one) && isRegExp(two))
        return compareRegExp(one, two);
    // Date: compare epoch time
    if (one instanceof Date && two instanceof Date)
        return one.getTime() === two.getTime();
    if (typeofOne === 'function')
        throw new BadImplementationException('This compare meant to compare two POJOs.. nothing more');
    if (typeofOne !== 'object')
        return one === two;
    if (Array.isArray(one) && Array.isArray(two)) {
        if (one.length !== two.length)
            return false;
        for (let i = 0; i < one.length; i++) {
            if (compare(one[i], two[i]))
                continue;
            return false;
        }
        return true;
    }
    const _one = one;
    const _two = two;
    const oneKeys = keys || Object.keys(_one);
    const twoKeys = keys || Object.keys(_two);
    if (oneKeys.length !== twoKeys.length)
        return false;
    for (const oneKey of oneKeys) {
        if (!twoKeys.includes(oneKey))
            return false;
    }
    for (const oneKey of oneKeys) {
        if (compare(_one[oneKey], _two[oneKey]))
            continue;
        return false;
    }
    return true;
}
export function assert(message, expected, actual) {
    if (!compare(expected, actual))
        throw new AssertionException(`Assertion Failed:\n  -- ${message}\n  -- Expected: ${JSON.stringify(expected)}\n  --   Actual: ${JSON.stringify(actual)}\n\n`);
}
/**
 * Removes keys from an object based on a filter function (in-place).
 *
 * **Important**: This modifies the object in-place by deleting matching keys.
 *
 * @param obj - Object to filter (modified in-place)
 * @param keys - Key(s) to check. Defaults to all keys in the object.
 * @param filter - Function that returns true for keys to remove. Defaults to removing
 *                 keys with undefined or null values.
 * @returns The modified object (same instance)
 * @throws {BadImplementationException} If obj is not an object
 */
export function filterKeys(obj, keys = _keys(obj), filter = (k) => obj[k] === undefined || obj[k] === null) {
    if (typeof obj !== 'object' || obj === null) {
        throw new BadImplementationException('Passed parameter for "obj" must be an object');
    }
    asArray(keys).forEach(key => {
        if (filter(key, obj))
            delete obj[key];
    });
    return obj;
}
const defaultScrubConfig = {
    emptyObjects: false,
    emptyArrays: false,
    emptyStrings: false,
    returnCopy: true
};
/**
 * Recursively removes "empty" values from objects and arrays.
 *
 * Scrubs an object/array by removing empty values based on configuration:
 * - Empty strings (if `emptyStrings: true`)
 * - Empty arrays (if `emptyArrays: true`)
 * - Empty objects (if `emptyObjects: true`)
 *
 * The scrubbing is recursive - nested objects and arrays are also scrubbed.
 * By default, returns a copy (`returnCopy: true`), but can modify in-place.
 *
 * **Note**: Booleans and numbers are never removed, even if falsy (0, false).
 *
 * @param item - Object or array to scrub
 * @param config - Configuration for what to scrub
 * @returns Scrubbed object/array, or undefined if the entire item was scrubbed
 */
export function scrub(item, config = {}) {
    config = merge(defaultScrubConfig, config);
    return scrubImpl(item, config);
}
function scrubImpl(item, config) {
    //Quick exit if item does not exist
    if (!exists(item))
        return;
    //Item is a boolean - always return it
    if (typeof item === 'boolean' || typeof item === 'number')
        return item;
    //Item is a string
    if (typeof item === 'string') {
        //return undefined if the item is an empty string and we are scrubbing those
        return (!item.length && config.emptyStrings) ? undefined : item;
    }
    //Item is an object
    if (typeof item === 'object') {
        if (Array.isArray(item)) { //ITEM IS AN ARRAY
            const arr = config.returnCopy ? cloneArr(item) : item;
            //Scrub each value in the array
            arr.forEach((val, i) => arr[i] = scrubImpl(val, config));
            //Remove any item that returned as undefined
            const filtered = arr.filter(item => !!item);
            filtered.forEach((val, i) => arr[i] = val);
            arr.length = filtered.length;
            //Return undefined if scrubbing empty arrays
            return (arr.length === 0 && config.emptyArrays) ? undefined : arr;
        }
        else { //ITEM IS AN OBJECT
            const obj = (config.returnCopy ? cloneObj(item) : item);
            const keys = _keys(obj);
            //Scrub each property on the object
            keys.forEach(key => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                obj[key] = scrubImpl(obj[key], config);
                if (!exists(obj[key]))
                    delete obj[key];
            });
            //Return undefined if scrubbing empty objects
            return (_keys(obj).length === 0 && config.emptyObjects) ? undefined : obj;
        }
    }
}
export function reduceObject(object, acc, reducer) {
    return _keys(object).reduce((accumulator, key) => {
        const typedKey = key;
        return reducer(accumulator, typedKey, object[typedKey]);
    }, acc);
}
/**
 * Recursively freezes an object and all its nested properties.
 *
 * Makes an object and all its nested objects/arrays immutable by calling
 * `Object.freeze()` recursively. Once frozen, properties cannot be added,
 * modified, or deleted.
 *
 * **Note**: Does not handle circular references - will cause stack overflow
 * if the object contains cycles.
 *
 * @param object - Object to freeze
 * @returns The frozen object (same instance)
 */
export function deepFreeze(object) {
    if (object === null || typeof object !== 'object')
        return object;
    // Freeze each property before freezing self
    for (const key of Object.getOwnPropertyNames(object)) {
        const value = object[key];
        if (typeof value === 'object' && value !== null && !Object.isFrozen(value))
            deepFreeze(value);
    }
    return Object.freeze(object);
}
//# sourceMappingURL=object-tools.js.map